"use client"

import { imageStorage } from "@/actions/image"
import { useGetTrainingByCode } from "@/actions/training"
import {
  useGetAllTypeTraining,
  useGetTypeAllTrainingByName,
} from "@/actions/type-training"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import useInput from "@/hooks/use-input"
import { capitalizeSentence, formatDotString, formatRupiah } from "@/lib/string"
import { cn } from "@/lib/tailwind-utils"
import Image from "next/image"
import Link from "next/link"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { useCallback, useMemo, useState } from "react"
import { FaStar, FaSearch, FaArrowRight } from "react-icons/fa"
import { Building2, BookOpen, ChevronRight, Filter } from "lucide-react"
import SectionWrapper from "../common/section-wrapper"
import { Button } from "../ui/button"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card"
import { Checkbox } from "../ui/checkbox"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Skeleton } from "../ui/skeleton"

const AccordPelatihan = ({
  name = "accord content",
  className,
  accordContent,
  defaultContent,
  icon: Icon,
  value = "item-1"
}) => {
  return (
    <Accordion
      className="w-full border-none"
      collapsible
      type="single"
      defaultValue={value}>
      <AccordionItem value={value} className="border-none">
        <AccordionTrigger className="hover:no-underline py-4">
          <div className="flex items-center gap-3">
             <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors duration-300">
               {Icon && <Icon className="w-4 h-4" />}
             </div>
             <span className="font-bold text-slate-800 text-sm tracking-tight">{name}</span>
          </div>
        </AccordionTrigger>
        <AccordionContent className={cn("pt-1 pb-4", className)}>
          <div className="space-y-1.5 pl-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
            {defaultContent}
            {accordContent ? accordContent : <h1 className="text-xs text-slate-400">Belum ada data</h1>}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}

export default function ShowPelatihan({ className, searchParams }) {
  const searchParamsLocal = useSearchParams()
  const pathname = usePathname()
  const router = useRouter()
  const [showMore, setShowMore] = useState(true)
  const [showMobileFilter, setShowMobileFilter] = useState(false)
  const { value, handleOnChange } = useInput()
  const {
    data: allTypeTraining,
    isLoading: isLoadingGetAllTypeTraining,
    isSuccess: isSuccessGetAllTypeTraining,
    // isError: isErrorGetAllTypeTraining,
  } = useGetAllTypeTraining()

  const [typeTrainingId, setTypeTrainingId] = useState(() =>
    searchParams?.type ? String(searchParams?.type) : "semua",
  )

  const {
    data: allTraining,
    isLoading: isLoadingGetTraining,
    isSuccess: isSuccessGetTraining,
  } = useGetTypeAllTrainingByName(typeTrainingId)

  const [trainingCode, setTrainingCode] = useState(() =>
    searchParams["training-code"]
      ? String(searchParams["training-code"])
      : "semua",
  )

  const targetParam = searchParamsLocal.get("target")

  const filteredAllTraining = useMemo(() => {
    if (!isSuccessGetTraining) return []
    if (!targetParam) {
      if (showMore && allTraining.length > 3) return allTraining.slice(0, 3)
      return allTraining
    }

    let result = allTraining
    if (targetParam === "aparatur") {
      result = allTraining.filter((t) => {
        const name = String(t.name).toLowerCase()
        return (
          name.includes("pns") ||
          name.includes("aparatur") ||
          name.includes("fungsional") ||
          name.includes("pimpinan") ||
          name.includes("kepemimpinan")
        )
      })
    } else if (targetParam === "umum") {
      result = allTraining.filter((t) => {
        const name = String(t.name).toLowerCase()
        // Exclude government-specific categories for the general public
        return !name.includes("pns") && !name.includes("aparatur")
      })
    }

    if (showMore && result.length > 3) return result.slice(0, 3)
    return result
  }, [allTraining, isSuccessGetTraining, targetParam, showMore])

  const filteredAllTypeTraining = useMemo(() => {
    if (!isSuccessGetAllTypeTraining) return []
    if (!targetParam) return allTypeTraining

    if (targetParam === "aparatur") {
      // Typically Aparatur units are Teknis or specific admin units
      return allTypeTraining.filter((type) => {
        const name = String(type.name).toLowerCase()
        return name.includes("teknis") || name.includes("aparatur") || name.includes("lainnya")
      })
    }
    
    return allTypeTraining
  }, [allTypeTraining, isSuccessGetAllTypeTraining, targetParam])

  const {
    data: allSubTraining,
    isLoading: isLoadingGetAllSubTraining,
    isSuccess: isSuccessGetAllSubTraining,
  } = useGetTrainingByCode(typeTrainingId, trainingCode)

  const handleSelectTypeTraining = (selectedTypeTraining) => {
    const params = new URLSearchParams(searchParamsLocal)
    if (typeTrainingId === selectedTypeTraining) {
      return
    }
    if (selectedTypeTraining === "semua") {
      setTrainingCode("semua")
    }

    if (params.get("training-code")) {
      params.set("training-code", "semua")
      setTrainingCode("semua")
    }

    params.set("type", selectedTypeTraining)
    setTypeTrainingId(selectedTypeTraining)
    router.replace(`${pathname}?${params.toString()}`)
  }

  const handleSelectTrainingCode = (selectedtrainingCode) => {
    const params = new URLSearchParams(searchParamsLocal)
    if (trainingCode === selectedtrainingCode) {
      return
    }
    params.set("training-code", selectedtrainingCode)
    setTrainingCode(selectedtrainingCode)
    router.replace(`${pathname}?${params.toString()}`)
  }

  const searchTraining = useMemo(() => {
    if (isSuccessGetAllSubTraining) {
      let filtered = allSubTraining.filter((subTrain) =>
        subTrain?.name?.toLowerCase().includes(value?.toLowerCase()),
      )

      if (targetParam === "aparatur") {
        filtered = filtered.filter((train) => {
          const stTarget = String(train?.target).toLowerCase()
          const subName = String(train?.name || "").toLowerCase()
          const parentName = String(train?.training?.name || "").toLowerCase()
          
          if (stTarget === "aparatur") return true
          if (stTarget === "semua") {
            const hasKeyword = (str) => 
              str.includes("pns") || 
              str.includes("aparatur") || 
              str.includes("fungsional") || 
              str.includes("pimpinan") || 
              str.includes("kepemimpinan")
            
            return hasKeyword(subName) || hasKeyword(parentName)
          }
          return false
        })
      } else if (targetParam === "umum") {
        filtered = filtered.filter((train) => {
          const stTarget = String(train?.target).toLowerCase()
          const subName = String(train?.name || "").toLowerCase()
          const parentName = String(train?.training?.name || "").toLowerCase()

          if (stTarget === "umum") return true
          if (stTarget === "semua") {
            const hasGovKeyword = (str) => str.includes("pns") || str.includes("aparatur")
            // Exclude primarily government-focused sub-trainings OR categories
            return !hasGovKeyword(subName) && !hasGovKeyword(parentName)
          }
          return false
        })
      }

      return filtered
    }

    return []
  }, [isSuccessGetAllSubTraining, value, allSubTraining, searchParamsLocal])

  // console.log({
  //   subTraining: searchTraining,
  // })


  return (
    <div className="relative">
      {/* Dekstop */}
      <SectionWrapper className={"md:block hidden"}>
        <div className="grid grid-cols-8 gap-x-10">
          <div className="col-span-2 space-y-4">
            <div className="bg-white rounded-[2rem] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100/50 sticky top-32">
              <div className="flex items-center gap-2 mb-6 pb-4 border-b border-slate-50">
                <div className="p-2 bg-blue-50 rounded-xl text-blue-600">
                   <Filter className="w-4 h-4" />
                </div>
                <h3 className="font-bold text-slate-800 tracking-tight">Filter Pencarian</h3>
              </div>

              <AccordPelatihan
                className="flex flex-col gap-1"
                name="Unit Diklat"
                icon={Building2}
                value="unit-diklat"
                defaultContent={
                  <section 
                    onClick={() => handleSelectTypeTraining("semua")}
                    className={cn(
                      "group/checkPelatihan flex items-center gap-x-3 p-2.5 rounded-xl cursor-pointer transition-all duration-200",
                      "semua" === typeTrainingId ? "bg-indigo-50/50 text-indigo-700" : "hover:bg-slate-50 text-slate-600"
                    )}>
                    <Checkbox
                      className={cn(
                        "h-4 w-4 rounded-md border-slate-300",
                        "semua" === typeTrainingId && "border-indigo-600 data-[state=checked]:bg-indigo-600"
                      )}
                      id="unit-semua"
                      checked={"semua" === typeTrainingId}
                    />
                    <Label
                      htmlFor="unit-semua"
                      className={cn(
                        "text-sm group-hover/checkPelatihan:cursor-pointer font-medium cursor-pointer w-full transition-colors",
                        "semua" === typeTrainingId ? "font-bold text-indigo-700" : "group-hover/checkPelatihan:text-slate-900",
                      )}>
                      {capitalizeSentence("Semua Unit")}
                    </Label>
                  </section>
                }
                accordContent={
                  <>
                    {isLoadingGetAllTypeTraining && <div className="p-4 text-xs text-slate-400 animate-pulse">Memuat Unit...</div>}
                    {isSuccessGetAllTypeTraining &&
                      filteredAllTypeTraining.map((trainingType) => (
                        <section
                          key={trainingType?.id}
                          onClick={() => handleSelectTypeTraining(String(trainingType?.id))}
                          className={cn(
                            "group/checkPelatihan flex items-center gap-x-3 p-2.5 rounded-xl cursor-pointer transition-all duration-200",
                            String(trainingType?.id) === typeTrainingId ? "bg-indigo-50/50 text-indigo-700" : "hover:bg-slate-50 text-slate-600"
                          )}>
                          <Checkbox
                            className={cn(
                              "h-4 w-4 rounded-md border-slate-300",
                              String(trainingType?.id) === typeTrainingId && "border-indigo-600 data-[state=checked]:bg-indigo-600"
                            )}
                            id={`unit-${trainingType?.id}`}
                            checked={String(trainingType?.id) === typeTrainingId}
                          />
                          <Label
                            htmlFor={`unit-${trainingType?.id}`}
                            className={cn(
                              "text-sm group-hover/checkPelatihan:cursor-pointer font-medium cursor-pointer w-full transition-colors",
                              String(trainingType?.id) === typeTrainingId ? "font-bold text-indigo-700" : "group-hover/checkPelatihan:text-slate-900",
                            )}>
                            {capitalizeSentence(trainingType?.name)}
                          </Label>
                        </section>
                      ))}
                  </>
                }
              />
              
              <div className="h-px bg-slate-50 my-2 mx-2"></div>

              <AccordPelatihan
                className="flex flex-col gap-1"
                name="Program Diklat"
                icon={BookOpen}
                value="program-diklat"
                defaultContent={
                  <section 
                    onClick={() => handleSelectTrainingCode("semua")}
                    className={cn(
                      "group/checkPelatihan flex items-center gap-x-3 p-2.5 rounded-xl cursor-pointer transition-all duration-200",
                      "semua" === trainingCode ? "bg-indigo-50/50 text-indigo-700" : "hover:bg-slate-50 text-slate-600"
                    )}>
                    <Checkbox
                      className={cn(
                        "h-4 w-4 rounded-md border-slate-300",
                        "semua" === trainingCode && "border-indigo-600 data-[state=checked]:bg-indigo-600"
                      )}
                      id="diklat-semua"
                      checked={"semua" === trainingCode}
                    />
                    <Label
                      htmlFor="diklat-semua"
                      className={cn(
                        "text-sm group-hover/checkPelatihan:cursor-pointer font-medium cursor-pointer w-full transition-colors",
                        "semua" === trainingCode ? "font-bold text-indigo-700" : "group-hover/checkPelatihan:text-slate-900",
                      )}>
                      {capitalizeSentence("Semua Program")}
                    </Label>
                  </section>
                }
                accordContent={
                  <>
                    {isLoadingGetTraining && <div className="p-4 text-xs text-slate-400 animate-pulse">Memuat...</div>}
                    {isSuccessGetTraining && (
                      <>
                        {filteredAllTraining.map((training) => (
                          <section
                            key={training?.id}
                            onClick={() => handleSelectTrainingCode(String(training?.code))}
                            className={cn(
                              "group/checkPelatihan flex items-center gap-x-3 p-2.5 rounded-xl cursor-pointer transition-all duration-200",
                              String(training?.code) === trainingCode ? "bg-indigo-50/50 text-indigo-700" : "hover:bg-slate-50 text-slate-600"
                            )}>
                            <Checkbox
                              className={cn(
                                "h-4 w-4 rounded-md border-slate-300",
                                String(training?.code) === trainingCode && "border-indigo-600 data-[state=checked]:bg-indigo-600"
                              )}
                              id={`diklat-${training?.code}`}
                              checked={String(training?.code) === trainingCode}
                            />
                            <Label
                              htmlFor={`diklat-${training?.code}`}
                              className={cn(
                                "text-sm group-hover/checkPelatihan:cursor-pointer font-medium cursor-pointer w-full transition-colors",
                                String(training?.code) === trainingCode ? "font-bold text-indigo-700" : "group-hover/checkPelatihan:text-slate-900",
                              )}>
                              {capitalizeSentence(training?.name)}
                            </Label>
                          </section>
                        ))}
                        <div className="pt-2 pl-2 border-t border-slate-50/50 mt-1">
                          <button
                            className="flex items-center gap-2 text-[13px] font-bold text-indigo-600 hover:text-indigo-800 transition-colors group/show"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowMore(!showMore);
                            }}>
                            {showMore ? "Lebih banyak" : "Lebih sedikit"}
                            <ChevronRight className={cn("w-3 h-3 transition-transform", !showMore && "rotate-90")} />
                          </button>
                        </div>
                      </>
                    )}
                  </>
                }
              />
            </div>
          </div>

          <div className="col-span-6 space-y-8">
            <div className="bg-gradient-to-br from-[#f8fafc] via-white to-[#eff6ff] p-6 sm:p-8 rounded-[2rem] border border-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] space-y-6 relative overflow-hidden">
              {/* decorative blobs */}
              <div className="absolute -top-24 right-0 w-64 h-64 bg-blue-400/20 rounded-full mix-blend-multiply filter blur-3xl"></div>
              <div className="absolute -bottom-24 left-0 w-64 h-64 bg-indigo-400/20 rounded-full mix-blend-multiply filter blur-3xl"></div>

              <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="space-y-1">
                   <h1 className="font-extrabold text-3xl text-transparent bg-clip-text bg-gradient-to-r from-blue-900 to-indigo-700 tracking-tight">
                     Jelajahi Pelatihan
                   </h1>
                   <p className="text-slate-500 text-sm font-medium">Temukan program diklat terbaik untuk karir Anda</p>
                </div>
                {isLoadingGetAllSubTraining ? (
                  <div className="bg-white/80 backdrop-blur-sm text-indigo-600 px-4 py-2 rounded-full text-sm font-bold shadow-sm border border-indigo-50 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></span>
                    Memuat data...
                  </div>
                ) : (
                  <div className="bg-white/80 backdrop-blur-sm text-indigo-600 px-4 py-2 rounded-full text-sm font-bold shadow-sm border border-indigo-50 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                    {searchTraining.length} Pelatihan
                  </div>
                )}
              </div>

              <div className="relative z-10 pt-2">
                  <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none pt-2">
                    <FaSearch className="text-indigo-400 text-lg" />
                  </div>
                  <Input
                    value={value}
                    onChange={(e) => handleOnChange(e?.target?.value)}
                    placeholder="Ketikkan nama atau kategori pelatihan..."
                    className="pl-14 h-14 bg-white/90 backdrop-blur-sm rounded-2xl border-white focus-visible:ring-indigo-500 focus-visible:ring-offset-2 w-full text-base shadow-[0_4px_20px_rgb(0,0,0,0.05)] transition-all duration-300 hover:bg-white hover:shadow-[0_4px_25px_rgb(0,0,0,0.08)] placeholder:text-slate-400 font-medium"
                  />
              </div>
            </div>
            
            {searchTraining.length > 0 && isSuccessGetAllSubTraining && (
              <div className="grid lg:grid-cols-2 md:grid-cols-2 xl:grid-cols-3 gap-8 pb-10">
                {searchTraining.map((subTraining) => {
                  return (
                    <Card
                      key={subTraining?.id}
                      className="group/card flex flex-col overflow-hidden border-0 shadow-[0_4px_20px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.12)] transition-all duration-500 rounded-[2rem] bg-white hover:-translate-y-2">
                       <div className="p-3 pb-0 z-10">
                         <div className="relative h-[240px] w-full overflow-hidden rounded-[1.5rem] bg-slate-100">
                             <Image
                               alt={subTraining.name}
                               src={imageStorage(subTraining.image)}
                               fill
                               sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                               style={{ objectFit: "cover" }}
                               className={cn(
                                 "transform transition-transform duration-700 group-hover/card:scale-110",
                               )}
                             />
                             <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a] via-[#0f172a]/20 to-transparent opacity-60 z-10 transition-opacity duration-500 group-hover/card:opacity-80"></div>

                             <div className="absolute top-4 left-4 z-20">
                               <div className="bg-white/20 backdrop-blur-md text-white text-[11px] font-bold px-3 py-1.5 rounded-full shadow-sm flex items-center gap-2 border border-white/20 uppercase tracking-widest">
                                 {formatDotString(subTraining.code, 60)}
                               </div>
                             </div>

                             <div className="absolute bottom-4 left-4 right-4 z-20 flex items-center justify-between">
                               <div className="flex items-center gap-x-1.5 bg-yellow-400 px-3 py-1.5 rounded-xl shadow-[0_4px_10px_rgba(0,0,0,0.2)]">
                                 <FaStar className="text-[12px] text-yellow-900" />
                                 <span className="text-xs font-bold text-yellow-900">
                                   5.0 <span className="font-medium opacity-80">(100+)</span>
                                 </span>
                               </div>
                             </div>
                         </div>
                      </div>

                      <CardContent className="p-6 flex-grow flex flex-col gap-4 bg-white relative z-20">
                        <div className="flex flex-col">
                          <h1
                            className="text-[17px] font-bold text-slate-800 line-clamp-2 leading-[1.4] group-hover/card:text-indigo-600 transition-colors duration-300"
                            title={subTraining.name}>
                            {subTraining.name}
                          </h1>
                        </div>

                        <div className="mt-auto flex items-center justify-between pt-5 border-t border-slate-100/80">
                          <div className="flex flex-col">
                            <span className="text-[11px] uppercase tracking-wider font-bold text-indigo-400/80 mb-1">
                              Biaya Pelatihan
                            </span>
                            <p className="text-[22px] font-black text-transparent bg-clip-text bg-gradient-to-br from-slate-800 to-indigo-900">
                              {formatRupiah(subTraining.top_price)}
                            </p>
                          </div>
                        </div>
                      </CardContent>

                      <CardFooter className="p-3 pt-0 relative z-20 bg-white">
                        <Button
                          asChild
                          className="w-full h-[54px] rounded-[1.2rem] font-bold text-[15px] bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-lg hover:shadow-indigo-200 transition-all duration-300 group/btn border-0 text-white overflow-hidden relative">
                          <Link
                            href={`/pelatihan/${String(subTraining?.code).toLowerCase()}`}
                            className="flex items-center justify-center gap-3">
                            <span className="relative z-10">Daftar Sekarang</span>
                            <div className="bg-white/20 p-2 rounded-xl group-hover/btn:bg-white/30 transition-colors relative z-10">
                                <FaArrowRight className="text-sm transition-transform group-hover/btn:translate-x-1" />
                            </div>
                          </Link>
                        </Button>
                      </CardFooter>
                    </Card>
                  )
                })}
              </div>
            )}
            {isLoadingGetAllSubTraining && (
              <div className="grid lg:grid-cols-2 md:grid-cols-2 xl:grid-cols-3 gap-8 pb-10">
                {Array.from({ length: 3 }).map((_, index) => {
                  return (
                    <Skeleton
                      key={index + 1}
                      className="group/card flex h-[460px] flex-col overflow-hidden rounded-[2rem]"
                    />
                  )
                })}
              </div>
            )}
            {searchTraining.length === 0 && isSuccessGetAllSubTraining && (
              <div className="flex flex-col items-center justify-center py-20 bg-gradient-to-br from-[#f8fafc] via-white to-[#eff6ff] rounded-[2rem] border border-white shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <div className="w-20 h-20 bg-indigo-50 rounded-full flex items-center justify-center mb-6 shadow-inner">
                   <FaSearch className="text-4xl text-indigo-300" />
                </div>
                <h2 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-900 to-indigo-700 mb-2">Tidak Ditemukan</h2>
                <p className="text-slate-500 text-center max-w-sm font-medium">Coba cari dengan kata kunci lain atau periksa filter pencarian Anda.</p>
              </div>
            )}
          </div>
        </div>
      </SectionWrapper>

      {/* Mobile */}
      <SectionWrapper className={"block md:hidden  space-y-10"}>
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-[#f8fafc] via-white to-[#eff6ff] p-5 rounded-[2rem] border border-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] space-y-5 relative overflow-hidden">
            <div className="relative z-10 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <h1 className="font-extrabold text-xl text-transparent bg-clip-text bg-gradient-to-r from-blue-900 to-indigo-700 tracking-tight">
                  Cari Pelatihan
                </h1>
                {isLoadingGetAllSubTraining ? (
                  <div className="bg-white/80 backdrop-blur-sm text-indigo-600 px-3 py-1.5 rounded-full text-[11px] font-bold shadow-sm border border-indigo-50 flex items-center gap-1.5 flex-shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse"></span>
                    Memuat...
                  </div>
                ) : (
                  <div className="bg-white/80 backdrop-blur-sm text-indigo-600 px-3 py-1.5 rounded-full text-[11px] font-bold shadow-sm border border-indigo-50 flex items-center gap-1.5 flex-shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                    {searchTraining.length} Pelatihan
                  </div>
                )}
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <FaSearch className="text-indigo-400 text-sm" />
                </div>
                <Input
                  value={value}
                  onChange={(e) => handleOnChange(e?.target?.value)}
                  placeholder="Ketikkan nama pelatihan..."
                  className="pl-10 h-12 bg-white/90 backdrop-blur-sm rounded-xl border-white focus-visible:ring-indigo-500 focus-visible:ring-offset-2 w-full text-sm shadow-[0_4px_20px_rgb(0,0,0,0.05)] transition-all hover:shadow-[0_4px_25px_rgb(0,0,0,0.08)] placeholder:text-slate-400 font-medium"
                />
              </div>
            </div>
          </div>
        </div>
        <div>
          {searchTraining.length > 0 && isSuccessGetAllSubTraining && (
            <div className=" grid grid-cols-1 gap-8 pb-20">
              {searchTraining.map((subTraining) => {
                return (
                  <Card
                    key={subTraining?.id}
                    className="group/card flex flex-col overflow-hidden border-0 shadow-[0_4px_20px_rgb(0,0,0,0.04)] transition-all duration-300 rounded-[2rem] bg-white">
                     <div className="p-3 pb-0 z-10">
                       <div className="relative h-[200px] w-full overflow-hidden rounded-[1.5rem] bg-slate-100">
                           <Image
                             alt={subTraining.name}
                             src={imageStorage(subTraining.image)}
                             fill
                             sizes="100vw"
                             style={{ objectFit: "cover" }}
                             className={cn(
                               "transform transition-transform duration-700 group-hover/card:scale-105",
                             )}
                           />
                           <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a] via-[#0f172a]/20 to-transparent opacity-60 z-10 transition-opacity duration-500"></div>

                           <div className="absolute top-4 left-4 z-20">
                             <div className="bg-white/20 backdrop-blur-md text-white text-[11px] font-bold px-3 py-1.5 rounded-full shadow-sm flex items-center gap-2 border border-white/20 uppercase tracking-widest">
                               {formatDotString(subTraining.code, 60)}
                             </div>
                           </div>

                           <div className="absolute bottom-4 left-4 right-4 z-20 flex items-center justify-between">
                             <div className="flex items-center gap-x-1.5 bg-yellow-400 px-3 py-1.5 rounded-xl shadow-[0_4px_10px_rgba(0,0,0,0.2)]">
                               <FaStar className="text-[12px] text-yellow-900" />
                               <span className="text-xs font-bold text-yellow-900">
                                 5.0 <span className="font-medium opacity-80">(100+)</span>
                               </span>
                             </div>
                           </div>
                       </div>
                    </div>

                    <CardContent className="p-5 flex-grow flex flex-col gap-4 bg-white relative z-20">
                      <div className="flex flex-col">
                        <h1
                          className="text-[16px] font-bold text-slate-800 line-clamp-2 leading-[1.4]"
                          title={subTraining.name}>
                          {subTraining.name}
                        </h1>
                      </div>

                      <div className="mt-auto flex items-center justify-between pt-4 border-t border-slate-100/80">
                        <div className="flex flex-col">
                          <span className="text-[10px] uppercase tracking-wider font-bold text-indigo-400/80 mb-1">
                            Biaya Pelatihan
                          </span>
                          <p className="text-[20px] font-black text-transparent bg-clip-text bg-gradient-to-br from-slate-800 to-indigo-900">
                            {formatRupiah(subTraining.top_price)}
                          </p>
                        </div>
                      </div>
                    </CardContent>

                    <CardFooter className="p-3 pt-0 relative z-20 bg-white">
                      <Button
                        asChild
                        className="w-full h-[54px] rounded-[1.2rem] font-bold text-[15px] bg-gradient-to-r from-blue-600 to-indigo-600 shadow-lg transition-all duration-300 border-0 text-white overflow-hidden relative">
                        <Link
                          href={`/pelatihan/${String(subTraining?.code).toLowerCase()}`}
                          className="flex items-center justify-center gap-3 w-full">
                          <span className="relative z-10 w-full text-center pr-[48px]">Daftar Sekarang</span>
                          <div className="bg-white/20 p-2.5 rounded-xl absolute right-[4px] z-10 flex items-center justify-center">
                              <FaArrowRight className="text-sm" />
                          </div>
                        </Link>
                      </Button>
                    </CardFooter>
                  </Card>
                )
              })}
            </div>
          )}
          {isLoadingGetAllSubTraining && (
            <div className=" grid grid-cols-1 gap-8 pb-20">
              {Array.from({ length: 3 }).map((_, index) => {
                return (
                  <Skeleton
                    key={index + 1}
                    className="group/card flex h-[460px] flex-col overflow-hidden rounded-[2rem]"
                  />
                )
              })}
            </div>
          )}
          {searchTraining.length === 0 && isSuccessGetAllSubTraining && (
            <div className="flex flex-col items-center justify-center py-16 bg-gradient-to-br from-[#f8fafc] via-white to-[#eff6ff] rounded-[2rem] border border-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] mt-4">
              <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mb-5 shadow-inner">
                 <FaSearch className="text-3xl text-indigo-300" />
              </div>
              <h2 className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-900 to-indigo-700 mb-2">Tidak Ditemukan</h2>
              <p className="text-slate-500 text-center max-w-[250px] text-sm font-medium">Coba cari dengan kata kunci lain atau ubah filter.</p>
            </div>
          )}
        </div>
      </SectionWrapper>

      {/* Mobile Filter Overlay */}
      {showMobileFilter && (
        <section className="fixed inset-0 z-[100] flex flex-col bg-slate-50/95 backdrop-blur-xl md:hidden animate-in fade-in slide-in-from-bottom duration-300">
          {/* Mobile Header */}
          <div className="flex items-center justify-between px-6 py-6 bg-white border-b border-slate-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
                <Filter className="w-5 h-5" />
              </div>
              <h1 className="text-xl font-bold text-slate-900 tracking-tight">Filter Pelatihan</h1>
            </div>
            <button 
              onClick={() => setShowMobileFilter(false)}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div className="bg-white rounded-3xl p-4 shadow-sm border border-white">
              <AccordPelatihan
                className="flex flex-col gap-1"
                name="Unit Diklat"
                icon={Building2}
                value="unit-diklat-mobile"
                defaultContent={
                  <section 
                    onClick={() => handleSelectTypeTraining("semua")}
                    className={cn(
                      "flex items-center gap-x-3 p-3 rounded-xl transition-colors",
                      "semua" === typeTrainingId ? "bg-indigo-50 text-indigo-700 font-bold" : "text-slate-600"
                    )}>
                    <Checkbox
                      className={cn("h-4 w-4", "semua" === typeTrainingId && "border-indigo-600 data-[state=checked]:bg-indigo-600")}
                      id="unit-semua-mob"
                      checked={"semua" === typeTrainingId}
                    />
                    <Label htmlFor="unit-semua-mob" className="text-sm w-full">Semua Unit</Label>
                  </section>
                }
                accordContent={
                  <div className="space-y-1">
                    {filteredAllTypeTraining.map((trainingType) => (
                      <section
                        key={trainingType?.id}
                        onClick={() => handleSelectTypeTraining(String(trainingType?.id))}
                        className={cn(
                          "flex items-center gap-x-3 p-3 rounded-xl transition-colors",
                          String(trainingType?.id) === typeTrainingId ? "bg-indigo-50 text-indigo-700 font-bold" : "text-slate-600"
                        )}>
                        <Checkbox
                          className={cn("h-4 w-4", String(trainingType?.id) === typeTrainingId && "border-indigo-600 data-[state=checked]:bg-indigo-600")}
                          id={`unit-${trainingType?.id}-mob`}
                          checked={String(trainingType?.id) === typeTrainingId}
                        />
                        <Label htmlFor={`unit-${trainingType?.id}-mob`} className="text-sm w-full">{capitalizeSentence(trainingType?.name)}</Label>
                      </section>
                    ))}
                  </div>
                }
              />

              <div className="h-px bg-slate-50 my-2"></div>

              <AccordPelatihan
                className="flex flex-col gap-1"
                name="Program Diklat"
                icon={BookOpen}
                value="program-diklat-mobile"
                defaultContent={
                  <section 
                    onClick={() => handleSelectTrainingCode("semua")}
                    className={cn(
                      "flex items-center gap-x-3 p-3 rounded-xl transition-colors",
                      "semua" === trainingCode ? "bg-indigo-50 text-indigo-700 font-bold" : "text-slate-600"
                    )}>
                    <Checkbox
                      className={cn("h-4 w-4", "semua" === trainingCode && "border-indigo-600 data-[state=checked]:bg-indigo-600")}
                      id="diklat-semua-mob"
                      checked={"semua" === trainingCode}
                    />
                    <Label htmlFor="diklat-semua-mob" className="text-sm w-full">Semua Program</Label>
                  </section>
                }
                accordContent={
                  <div className="space-y-1">
                    {filteredAllTraining.map((training) => (
                      <section
                        key={training?.id}
                        onClick={() => handleSelectTrainingCode(String(training?.code))}
                        className={cn(
                          "flex items-center gap-x-3 p-3 rounded-xl transition-colors",
                          String(training?.code) === trainingCode ? "bg-indigo-50 text-indigo-700 font-bold" : "text-slate-600"
                        )}>
                        <Checkbox
                          className={cn("h-4 w-4", String(training?.code) === trainingCode && "border-indigo-600 data-[state=checked]:bg-indigo-600")}
                          id={`diklat-${training?.code}-mob`}
                          checked={String(training?.code) === trainingCode}
                        />
                        <Label htmlFor={`diklat-${training?.code}-mob`} className="text-sm w-full">{capitalizeSentence(training?.name)}</Label>
                      </section>
                    ))}
                  </div>
                }
              />
            </div>
          </div>

          <div className="p-6 bg-white border-t border-slate-100">
             <Button 
               onClick={() => setShowMobileFilter(false)}
               className="w-full h-[56px] rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-base shadow-lg shadow-indigo-200"
             >
               Terapkan Filter
             </Button>
          </div>
        </section>
      )}

      {/* Floating Filter Button */}
      <button
        onClick={() => setShowMobileFilter(true)}
        className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[90] flex items-center gap-3 px-6 py-4 bg-slate-900 text-white rounded-full shadow-2xl shadow-indigo-500/30 hover:bg-slate-800 transition-all duration-300 md:hidden scale-100 hover:scale-105 active:scale-95 border border-white/10 backdrop-blur-md"
      >
        <div className="p-1.5 bg-white/20 rounded-lg">
           <Filter className="w-4 h-4" />
        </div>
        <span className="font-bold tracking-wide">Ubah Filter</span>
      </button>
    </div>
  )
}
